import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { notificationsAPI } from '../services/api';
import { Bell, CheckCircle2, ArrowRight, UserCog, ShieldCheck, Filter } from 'lucide-react';

const Notifications = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    loadNotifications();
  }, [filter]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationsAPI.getAll({ 
        unread_only: filter === 'unread' ? 'true' : 'false' 
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      loadNotifications();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      loadNotifications();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const navigateToLeadDetail = (leadId) => {
    const returnTo = `${location.pathname}${location.search}` || '/notifications';
    const returnScroll = typeof window !== 'undefined' ? window.scrollY : 0;
    navigate(`/leads/${leadId}`, { state: { returnTo, returnScroll } });
  };

  const handleClickNotification = async (notification) => {
    try {
      if (!notification.is_read) {
        await notificationsAPI.markAsRead(notification.id);
      }
      if (notification.related_id) {
        navigateToLeadDetail(notification.related_id);
      } else {
        loadNotifications();
      }
    } catch (error) {
      console.error('Failed to open notification:', error);
    }
  };

  const getTypeMeta = (type) => {
    const key = String(type || '').toLowerCase();
    if (key.includes('queue_alert')) return { tone: 'bg-amber-50 border-amber-200', tag: 'Queue alert' };
    if (key.includes('reassigned_away')) return { tone: 'bg-rose-50 border-rose-200', tag: 'Reassigned away' };
    if (key.includes('reassigned_to') || key.includes('lead_assigned')) return { tone: 'bg-emerald-50 border-emerald-200', tag: 'Assigned' };
    return { tone: 'bg-slate-50 border-slate-200', tag: 'Update' };
  };

  const visibleNotifications = notifications.filter((notification) => {
    if (typeFilter === 'all') return true;
    return String(notification?.type || '').toLowerCase() === typeFilter;
  });

  const formatRelativeTime = (timestamp) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="app-page">
        <div className="bg-white border border-gray-200 rounded-md p-6">
          <div className="space-y-3">
            <div className="skeleton-shimmer h-6 w-56 rounded-md" />
            <div className="skeleton-shimmer h-4 w-72 rounded-md" />
          </div>
          <div className="mt-6 space-y-4">
            {[1, 2, 3, 4].map((row) => (
              <div key={row} className="rounded-xl border border-slate-200 bg-white/80 p-4">
                <div className="skeleton-shimmer h-4 w-1/3 rounded mb-2" />
                <div className="skeleton-shimmer h-4 w-2/3 rounded mb-2" />
                <div className="skeleton-shimmer h-3 w-1/4 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page">
      <div className="app-page-header">
        <div>
          <h1 className="app-page-title">Notifications</h1>
          <p className="app-page-subtitle">
            {isAdmin ? 'Coordinate assignments and queue alerts with agents' : 'Track assignments and lead updates in real time'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        {notifications.some(n => !n.is_read) && (
          <button
            onClick={handleMarkAllAsRead}
            className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            Mark All as Read
          </button>
        )}
          <button
            onClick={() => navigate('/leads')}
            className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors inline-flex items-center gap-2"
          >
            Lead Board
            <ArrowRight size={14} />
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md p-4 mb-6">
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Notifications
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'unread'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Unread Only
          </button>
          <div className="ml-auto flex items-center gap-2">
            <Filter size={14} className="text-slate-500" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="ui-control"
            >
              <option value="all">All types</option>
              <option value="lead_assigned">Assigned</option>
              <option value="lead_reassigned_to">Reassigned To</option>
              <option value="lead_reassigned_away">Reassigned Away</option>
              <option value="queue_alert">Queue Alerts</option>
            </select>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {visibleNotifications.length > 0 ? (
          visibleNotifications.map((notification) => {
            const typeMeta = getTypeMeta(notification.type);
            return (
            <div
              key={notification.id}
              className={`w-full text-left rounded-xl border p-4 transition-all ${typeMeta.tone} ${
                notification.is_read 
                  ? 'opacity-85' 
                  : 'shadow-sm'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Bell className="text-blue-600" size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        {notification.lead_name || notification.title || 'Notification'}
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-white/90 border border-slate-200 text-slate-700">
                          {typeMeta.tag}
                        </span>
                        {!notification.is_read && (
                          <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                            New
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">{formatRelativeTime(notification.created_at)}</p>
                    </div>
                    {!notification.is_read && <CheckCircle2 size={16} className="text-blue-600" />}
                  </div>
                  <p className="text-gray-700">{notification.message}</p>
                  <div className="mt-2 flex items-start justify-between gap-3 flex-wrap">
                    <p className="text-xs text-gray-500">{notification.project_name || 'General'}</p>
                    <div className="flex items-center gap-2">
                      {!notification.is_read && (
                        <button
                          onClick={() => handleMarkAsRead(notification.id)}
                          className="px-2.5 py-1 rounded-md border border-slate-300 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          Mark read
                        </button>
                      )}
                      {isAdmin && String(notification.type || '').includes('queue_alert') && (
                        <button
                          onClick={() => navigate('/leads?status=queued_unassigned')}
                          className="px-2.5 py-1 rounded-md bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 inline-flex items-center gap-1"
                        >
                          <UserCog size={12} />
                          Assign now
                        </button>
                      )}
                      {notification.related_id && (
                        <button
                          onClick={() => handleClickNotification(notification)}
                          className="px-2.5 py-1 rounded-md bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 inline-flex items-center gap-1"
                        >
                          <ShieldCheck size={12} />
                          Open lead
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )})
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Bell className="mx-auto mb-4 text-gray-400" size={48} />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No notifications</h3>
            <p className="text-gray-600">You're all caught up!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;